import { DEAL_OPERATIONS, formatPhone, leadSchema, type DealOperation } from '@erp/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import {
  emptyInterest,
  InterestFields,
  interestBody,
  type InterestValue,
} from '../../components/deals/interest-fields.tsx';
import { Alert } from '../../components/ui/alert.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Card } from '../../components/ui/card.tsx';
import { Combobox } from '../../components/ui/combobox.tsx';
import { Field, Input, Select } from '../../components/ui/field.tsx';
import { api, errorMessage } from '../../lib/api.ts';
import {
  useAssignees,
  useChannels,
  useContact,
  useContacts,
  type DuplicateMatch,
} from '../../lib/contacts-api.ts';
import { dealKeys, type DealDetail } from '../../lib/deals-api.ts';
import { useSession } from '../../lib/session.ts';
import { useToast } from '../../lib/toast.tsx';

/**
 * HU-CRM-01: a lead that arrived by phone, visit or at the office. A new contact or
 * an existing one (proposed when the phone or email already exists), plus the deal in
 * the first stage, assigned to whoever registers it.
 */
export function NewDealPage() {
  const { data: session } = useSession();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const client = useQueryClient();
  const notify = useToast();
  const channels = useChannels();
  const assignees = useAssignees();
  const [params] = useSearchParams();
  const preset = params.get('contacto');
  const presetContact = useContact(preset ?? '', Boolean(preset));
  const [existing, setExisting] = useState<{ id: string; name: string } | null>(null);
  const [mode, setMode] = useState<'new' | 'existing'>(preset ? 'existing' : 'new');
  // From a contact's card: that contact, already chosen.
  if (preset && presetContact.data && !existing && mode === 'existing') {
    setExisting({ id: presetContact.data.id, name: presetContact.data.name });
    setQuery(presetContact.data.name);
  }
  const results = useContacts(
    query.trim() ? { q: query.trim() } : {},
    mode === 'existing' && query.trim().length > 0,
  );
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [duplicates, setDuplicates] = useState<DuplicateMatch[]>([]);
  const [operation, setOperation] = useState<DealOperation>('purchase');
  const [channelId, setChannelId] = useState('');
  const [amount, setAmount] = useState('');
  const [assignee, setAssignee] = useState(session?.membership.id ?? '');
  const [interest, setInterest] = useState<InterestValue>(emptyInterest);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const checkDuplicates = async () => {
    const body = { phones: phone.trim() ? [phone] : [], emails: email.trim() ? [email] : [] };
    if (body.phones.length === 0 && body.emails.length === 0) {
      setDuplicates([]);
      return;
    }
    try {
      setDuplicates(await api<DuplicateMatch[]>('/contacts/duplicates', { method: 'POST', body }));
    } catch {
      // Only a hint.
    }
  };

  const submit = async () => {
    setError(null);
    const body = {
      ...(mode === 'existing' && existing
        ? { contactId: existing.id }
        : {
            newContact: {
              name,
              phones: phone.trim() ? [{ number: phone }] : [],
              emails: email.trim() ? [{ address: email }] : [],
              ...(channelId ? { channelId } : {}),
            },
          }),
      deal: {
        operation,
        channelId: channelId || null,
        estimatedAmount: amount.trim() ? Number(amount) : null,
        ...(assignee ? { assignedMembershipId: assignee } : {}),
        interest: interestBody(interest),
      },
    };
    const parsed = leadSchema.safeParse(body);
    if (!parsed.success) {
      setErrors(
        Object.fromEntries(
          parsed.error.issues.map((i) => [
            String(
              i.path.at(-1) === 'number' || i.path.includes('phones') ? 'phone' : i.path.at(-1),
            ),
            i.message,
          ]),
        ),
      );
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      const deal = await api<DealDetail>('/deals/lead', { method: 'POST', body });
      await client.invalidateQueries({ queryKey: dealKeys.all });
      await client.invalidateQueries({ queryKey: ['contacts'] });
      notify('Lead registrado.', 'success');
      await navigate(`/negocios/${deal.id}`);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div>
        <Link to="/negocios" className="text-sm text-primary hover:underline">
          ← Negocios
        </Link>
        <h1 className="text-2xl font-semibold">Nuevo lead</h1>
      </div>
      <form
        className="space-y-6"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {error && <Alert tone="danger">{error}</Alert>}
        <Card className="space-y-4">
          <h2 className="font-semibold">Cliente</h2>
          <fieldset className="flex gap-5">
            <legend className="sr-only">Contacto</legend>
            {(['new', 'existing'] as const).map((m) => (
              <label key={m} className="flex min-h-11 items-center gap-2 text-sm">
                <input
                  type="radio"
                  className="size-5"
                  checked={mode === m}
                  onChange={() => {
                    setMode(m);
                  }}
                />
                {m === 'new' ? 'Contacto nuevo' : 'Ya está en la cartera'}
              </label>
            ))}
          </fieldset>
          {mode === 'existing' ? (
            <Field label="Buscar contacto" error={errors.contactId}>
              {(a11y) => (
                <Combobox
                  id={a11y.id}
                  describedBy={a11y['aria-describedby']}
                  query={query}
                  onQueryChange={(v) => {
                    setQuery(v);
                    setExisting(null);
                  }}
                  loading={results.isFetching}
                  options={(results.data?.items ?? []).map((c) => ({
                    id: c.id,
                    label: c.name,
                    detail: [c.phone && formatPhone(c.phone), c.email].filter(Boolean).join(' · '),
                  }))}
                  onSelect={(option) => {
                    setExisting({ id: option.id, name: option.label });
                    setQuery(option.label);
                  }}
                  placeholder="Nombre, teléfono o correo"
                />
              )}
            </Field>
          ) : (
            <>
              <Field label="Nombre completo" error={errors.name}>
                {(a11y) => (
                  <Input
                    {...a11y}
                    autoComplete="off"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                    }}
                  />
                )}
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Teléfono" error={errors.phone ?? errors.phones}>
                  {(a11y) => (
                    <Input
                      {...a11y}
                      type="tel"
                      inputMode="tel"
                      placeholder="7123 4567"
                      value={phone}
                      onChange={(e) => {
                        setPhone(e.target.value);
                      }}
                      onBlur={() => void checkDuplicates()}
                    />
                  )}
                </Field>
                <Field label="Correo" error={errors.address}>
                  {(a11y) => (
                    <Input
                      {...a11y}
                      type="email"
                      inputMode="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                      }}
                      onBlur={() => void checkDuplicates()}
                    />
                  )}
                </Field>
              </div>
              {duplicates.length > 0 && (
                <Alert tone="warning" title="Este cliente ya existe">
                  <ul className="mt-1 space-y-1">
                    {duplicates.map((d, i) => (
                      <li
                        key={d.contact?.id ?? String(i)}
                        className="flex flex-wrap items-center gap-2"
                      >
                        {d.contact ? (
                          <>
                            <span className="font-medium">{d.contact.name}</span>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => {
                                if (!d.contact) return;
                                setExisting(d.contact);
                                setQuery(d.contact.name);
                                setMode('existing');
                                setDuplicates([]);
                              }}
                            >
                              Usar este contacto
                            </Button>
                          </>
                        ) : (
                          <span>
                            Está en la cartera de {d.assignedTo}: pídele que te lo comparta.
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </Alert>
              )}
            </>
          )}
        </Card>

        <Card className="space-y-4">
          <h2 className="font-semibold">Negocio</h2>
          <fieldset className="flex gap-5">
            <legend className="mb-1 text-sm font-medium">Busca</legend>
            {(Object.entries(DEAL_OPERATIONS) as [DealOperation, string][]).map(([op, label]) => (
              <label key={op} className="flex min-h-11 items-center gap-2 text-sm">
                <input
                  type="radio"
                  className="size-5"
                  checked={operation === op}
                  onChange={() => {
                    setOperation(op);
                  }}
                />
                {label}
              </label>
            ))}
          </fieldset>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Canal">
              {(a11y) => (
                <Select
                  {...a11y}
                  value={channelId}
                  onChange={(e) => {
                    setChannelId(e.target.value);
                  }}
                >
                  <option value="">Sin canal</option>
                  {(channels.data ?? [])
                    .filter((c) => c.active)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                </Select>
              )}
            </Field>
            <Field label="Monto estimado (USD)" error={errors.estimatedAmount}>
              {(a11y) => (
                <Input
                  {...a11y}
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                  }}
                />
              )}
            </Field>
            <Field label="Agente">
              {(a11y) => (
                <Select
                  {...a11y}
                  value={assignee}
                  disabled={(assignees.data?.length ?? 0) < 2}
                  onChange={(e) => {
                    setAssignee(e.target.value);
                  }}
                >
                  {(assignees.data ?? []).map((m) => (
                    <option key={m.membershipId} value={m.membershipId}>
                      {m.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
        </Card>

        {/* A captación brings a property in: there is nothing it looks for. */}
        {operation !== 'listing' && (
          <Card className="space-y-4">
            <h2 className="font-semibold">Qué busca</h2>
            <InterestFields value={interest} onChange={setInterest} errors={errors} />
          </Card>
        )}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Link
            to="/negocios"
            className="inline-flex min-h-11 items-center justify-center rounded-lg px-4 text-sm font-medium hover:bg-muted"
          >
            Cancelar
          </Link>
          <Button variant="brand" type="submit" loading={busy}>
            Registrar lead
          </Button>
        </div>
      </form>
    </div>
  );
}
